import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { useLang } from "../../../i18n/LanguageContext";
import { dreamsClient } from "../api/dreamsClient";
import { sanitizeDreamHtml } from "../utils/safeDreamHtml";
import {
  ensureQuickSaveDraft,
  listQuickSaveDrafts,
  readQuickSaveDraft,
  removeQuickSaveDraft,
  saveQuickSaveDraft,
  writeQuickSaveActiveId,
} from "../utils/quickSaveDrafts";
import { createQuickSaveVoiceController, quickSaveVoiceSupported } from "../utils/quickSaveVoice";

const SAVE_MODE = "e2e";

const today = () => new Date().toISOString().slice(0, 10);
const nowTime = () => new Date().toTimeString().slice(0, 5);

const copyFor = (lang) => ({
  title: lang === "en" ? "Quick dream catcher" : "Szybki zapis snu",
  subtitle: lang === "en"
    ? "Catch the dream before it fades. Speak first, polish it later."
    : "Złap sen, zanim się rozproszy. Mów od razu, dopracujesz go później.",
  placeholder: lang === "en" ? "Describe your dream…" : "Opisz swój sen…",
  noteHint: lang === "en" ? "Speak or write — catch every important trace." : "Mów lub pisz — uchwyć każdą ważną chwilę.",
  speak: lang === "en" ? "Speak" : "Mów",
  stop: lang === "en" ? "Stop" : "Zatrzymaj",
  micTest: lang === "en" ? "Microphone test" : "Test mikrofonu",
  deleteDraft: lang === "en" ? "Delete note" : "Usuń szkic",
  saveCodex: lang === "en" ? "Save in Dream Codex" : "Zapisz w Księdze Snów",
  home: lang === "en" ? "Home" : "Strona główna",
  savedDraft: lang === "en" ? "Draft saved ✓" : "Zapisano szkic ✓",
  deleted: lang === "en" ? "Draft deleted." : "Usunięto szkic.",
  empty: lang === "en" ? "Empty draft." : "Pusty szkic.",
  localNote: lang === "en"
    ? "This quick note stays only on this device until you save it in your Dream Codex."
    : "Ten szybki zapis zostaje tylko na tym urządzeniu, dopóki nie zapiszesz go w Księdze Snów.",
  signIn: lang === "en" ? "Sign in to save this draft in your Dream Codex." : "Zaloguj się, żeby zapisać ten szkic w Księdze Snów.",
  opening: lang === "en" ? "Opening the gate…" : "Otwieram bramę…",
  preparing: lang === "en" ? "Preparing the entry…" : "Przygotowuję wpis…",
  saving: lang === "en" ? "Saving in Dream Codex…" : "Zapisuję w Księdze Snów…",
  saved: lang === "en" ? "Dream saved. Opening edit view…" : "Sen zapisany. Otwieram edycję…",
  notReady: lang === "en" ? "The Dream Codex is not ready yet. Refresh the page and try again." : "Księga Snów nie jest jeszcze gotowa. Odśwież stronę i spróbuj ponownie.",
  openFailed: lang === "en" ? "Could not open the Dream Codex. Try again." : "Nie udało się otworzyć Księgi Snów. Spróbuj ponownie.",
  saveFailed: lang === "en" ? "Could not save the dream. Try again." : "Nie udało się zapisać snu. Spróbuj ponownie.",
  micUnsupported: lang === "en" ? "Speech recognition is not available in this browser." : "Rozpoznawanie mowy nie jest dostępne w tej przeglądarce.",
});

const voiceCopyFor = (lang) => ({
  unsupported: lang === "en" ? "Dictation works best in Chrome/Android." : "Dyktowanie: dostępne głównie w Chrome/Android.",
  ask: lang === "en" ? "Asking for microphone access…" : "Proszę o dostęp do mikrofonu…",
  ready: lang === "en" ? "Microphone test: works ✓" : "Test mikrofonu: działa ✓",
  blocked: lang === "en" ? "Microphone access is blocked." : "Brak zgody na mikrofon.",
  listening: lang === "en" ? "Speak now…" : "Mów teraz…",
  hearing: lang === "en" ? "Listening…" : "Słucham…",
  waiting: lang === "en" ? "Pause after silence — waiting for you to continue…" : "Pauza po ciszy — czekam na kontynuację…",
  noSpeech: lang === "en" ? "Silence — waiting for you to continue…" : "Cisza — czekam na kontynuację…",
  resume: lang === "en" ? "Resuming dictation…" : "Wznawiam dyktowanie…",
  stopped: lang === "en" ? "Dictation stopped." : "Zatrzymano dyktowanie.",
  stopAfterSilence: lang === "en" ? "Stopped after 2 minutes of silence — press Speak to resume." : "Zakończono po 2 minutach ciszy — naciśnij Mów, aby wznowić.",
  added: lang === "en" ? "Added from speech ✓" : "Dodano z mowy ✓",
  undo: lang === "en" ? "Last insertion undone." : "Cofnięto ostatnie wstawienie.",
  clear: lang === "en" ? "Note cleared." : "Wyczyszczono notatkę.",
  error: lang === "en" ? "Dictation error: " : "Błąd dyktowania: ",
  https: lang === "en" ? "Dictation requires HTTPS." : "Dyktowanie wymaga HTTPS.",
});

const escapeHtml = (value) => String(value || "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/\"/g, "&quot;")
  .replace(/'/g, "&#039;");

const textToHtml = (text) => sanitizeDreamHtml(String(text || "").trim().split(/\n{2,}/).map((part) => `<p>${escapeHtml(part).replace(/\n/g, "<br>")}</p>`).join(""));
const titleFromText = (text, copy) => {
  const title = String(text || "").replace(/\s+/g, " ").trim();
  if (!title) return copy.title;
  return title.length > 60 ? title.slice(0, 60) : title;
};

export const QuickSavePage = () => {
  const { lang } = useLang();
  const { isAuthenticated, authReady } = useAuth();
  const copy = useMemo(() => copyFor(lang), [lang]);
  const voiceCopy = useMemo(() => voiceCopyFor(lang), [lang]);
  const textareaRef = useRef(null);
  const voiceRef = useRef(null);
  const saveTimerRef = useRef(0);
  const textRef = useRef("");
  const activeIdRef = useRef("");
  const [text, setText] = useState("");
  const [status, setStatus] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const setCurrentDraft = useCallback((nextId, nextText) => {
    const normalizedText = String(nextText || "");
    activeIdRef.current = nextId || "";
    textRef.current = normalizedText;
    setText(normalizedText);
  }, []);

  const refresh = useCallback(async (preferredId = "") => {
    const state = await ensureQuickSaveDraft();
    const nextId = preferredId || state.activeId;
    const active = nextId ? await readQuickSaveDraft(nextId) : state.activeDraft;
    setCurrentDraft(nextId || "", active?.text || "");
  }, [setCurrentDraft]);

  const saveDraft = useCallback(async (nextText = textRef.current, message = copy.savedDraft) => {
    const draftId = activeIdRef.current;
    if (!draftId) return;
    const current = await readQuickSaveDraft(draftId);
    await saveQuickSaveDraft({ ...(current || { id: draftId, createdAt: Date.now() }), text: nextText, updatedAt: Date.now() });
    if (message) setStatus(message);
  }, [copy.savedDraft]);

  const scheduleSave = useCallback((nextText) => {
    window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      saveDraft(nextText, "").catch((error) => setStatus(error?.message || copy.saveFailed));
    }, 450);
  }, [copy.saveFailed, saveDraft]);

  const setTextAndSave = useCallback((next) => {
    const normalizedText = String(next || "");
    textRef.current = normalizedText;
    setText(normalizedText);
    scheduleSave(normalizedText);
  }, [scheduleSave]);

  useEffect(() => {
    refresh().catch((error) => setStatus(error?.message || copy.saveFailed));
    return () => window.clearTimeout(saveTimerRef.current);
  }, [copy.saveFailed, refresh]);

  useEffect(() => {
    voiceRef.current?.destroy?.();
    const controller = createQuickSaveVoiceController({ lang, textareaRef, getText: () => textRef.current, setText: setTextAndSave, setStatus, copy: voiceCopy });
    const unsubscribe = controller.onStateChange((state) => setIsListening(state !== "off"));
    voiceRef.current = controller;
    return () => {
      unsubscribe();
      controller.destroy();
    };
  }, [lang, setTextAndSave, voiceCopy]);

  const handleDeleteDraft = async () => {
    window.clearTimeout(saveTimerRef.current);
    if (activeIdRef.current) {
      await removeQuickSaveDraft(activeIdRef.current);
      writeQuickSaveActiveId("");
    }
    await listQuickSaveDrafts();
    await refresh("");
    setStatus(copy.deleted);
  };

  const handleSaveToCodex = async () => {
    const rawText = String(textRef.current || "").trim();
    if (!rawText) {
      setStatus(copy.empty);
      return;
    }
    window.clearTimeout(saveTimerRef.current);
    await saveDraft(rawText, "");
    if (!authReady || !isAuthenticated) {
      setStatus(copy.signIn);
      window.location.hash = "#/zaloguj?redirect_to=/szybki-zapis";
      return;
    }
    if (!window.MDVaultSession || !window.morfeuszE2E) {
      setStatus(copy.notReady);
      return;
    }
    if (isSaving) return;
    setIsSaving(true);
    try {
      setStatus(copy.opening);
      const unlocked = await window.MDVaultSession.ensureUnlocked();
      const ok = unlocked?.ok || unlocked?.state?.unlocked || window.MDVaultSession?.isUnlocked?.() || window.MDVaultSession?.getState?.()?.unlocked;
      if (!ok) {
        setStatus(copy.openFailed);
        return;
      }
      const title = titleFromText(rawText, copy);
      setStatus(copy.preparing);
      const encrypted = await window.morfeuszE2E.encryptText(textToHtml(rawText));
      setStatus(copy.saving);
      const response = await dreamsClient.create({ title, date: today(), time: nowTime(), saveMode: SAVE_MODE, bodyHtml: "", e2e: encrypted, symbols: "", emotions: [], sen_tytul: title, sen_opis: "", sen_data: today(), sen_godzina: nowTime(), sen_symbole: "", sen_emocje: [], save_mode: SAVE_MODE });
      const dreamId = response?.dream?.id || response?.dream?.wpId;
      if (activeIdRef.current) await removeQuickSaveDraft(activeIdRef.current);
      writeQuickSaveActiveId("");
      await refresh("");
      setStatus(copy.saved);
      window.location.hash = dreamId ? `#/sen/${encodeURIComponent(dreamId)}?edit=1` : "#/moje-sny";
    } catch (error) {
      setStatus(error?.message || copy.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="dream-container" id="szybki-zapis" data-ui="quick-save-root">
      <header className="dream-header">
        <div className="dream-icon" aria-hidden="true">🌙</div>
        <h1 className="dream-title">{copy.title}</h1>
        <div className="dream-date">{copy.subtitle}</div>
      </header>
      <div className="md-alert md-info" data-ui="quick-save-banner">{copy.localNote}</div>
      <div className="dream-content-card" data-ui="quick-save-card">
        <div className="field" data-ui="qd-field">
          <textarea ref={textareaRef} id="mrf-qd-text" className="md-input" data-ui="qd-text" value={text} onChange={(event) => setTextAndSave(event.target.value)} placeholder={copy.placeholder} rows={12} autoFocus />
          {!text && <div data-ui="qd-empty-hint"><span>{copy.placeholder}</span><small>{copy.noteHint}</small></div>}
        </div>
        {status && <div className="md-alert md-info" data-ui="qd-status" role="status" aria-live="polite">{status}</div>}
        <div className="form-actions" data-ui="qd-actions">
          <button type="button" className="md-btn md-primary" data-ui="qd-voice-primary" onClick={() => voiceRef.current?.toggle?.()} aria-pressed={isListening ? "true" : "false"}>{isListening ? copy.stop : copy.speak}</button>
          <button type="button" className="md-btn md-secondary" data-ui="btn-ghost" onClick={() => quickSaveVoiceSupported() ? voiceRef.current?.test?.() : setStatus(copy.micUnsupported)}>{copy.micTest}</button>
          <button type="button" className="md-btn md-secondary" data-ui="btn-danger" onClick={handleDeleteDraft}>{copy.deleteDraft}</button>
          <button type="button" className="md-btn md-primary" data-ui="qd-save-codex" onClick={handleSaveToCodex} disabled={isSaving}>{isSaving ? copy.saving : copy.saveCodex}</button>
          <a className="md-btn md-secondary" data-ui="btn-ghost" href="#top">{copy.home}</a>
        </div>
      </div>
    </section>
  );
};
